import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Sparkles, X } from "lucide-react";
import { api, optimg } from "@/lib/api";
import { MaskLine, Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1722963295936-03ea25f1d4d2?auto=format&fit=crop&w=900&q=72";
const MANI = "https://images.unsplash.com/photo-1534953342533-7711c98712be?auto=format&fit=crop&w=800&q=72";

const INTENTS = [
  { label: "For Her", to: "/shop?recipient=For Her", img: "https://images.unsplash.com/photo-1615654673360-29898e4f857f?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { label: "For Him", to: "/shop?recipient=For Him", img: "https://images.unsplash.com/photo-1517430536-ef20313aea20" },
  { label: "Anniversary", to: "/shop?occasion=Anniversary", img: "https://images.unsplash.com/photo-1677658288237-2fff291a34f0" },
  { label: "Diwali", to: "/festival/diwali", img: "https://images.unsplash.com/photo-1577083753695-e010191bacb5?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { label: "For Kids", to: "/shop?recipient=For Kids", img: "https://images.unsplash.com/photo-1605735940798-5201db29e5e9?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { label: "Raksha Bandhan", to: "/festival/raksha-bandhan", img: "https://images.unsplash.com/photo-1716400128984-3681f2005d22?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

const CHAPTERS = [
  { n: "01", t: "Made by real hands", d: "Every piece is crafted by an independent maker somewhere in India — never mass-produced, never soulless." },
  { n: "02", t: "Priced fairly", d: "Zero listing fees. Makers keep more of what they earn, and you pay for craft, not middlemen." },
  { n: "03", t: "Delivered with care", d: "Each product carries its own honest delivery promise, so you always know when love arrives." },
];

function nextFestival() {
  const today = new Date();
  const defs = [
    { slug: "valentines-day", label: "Valentine's Edit", copy: "handmade romance — bouquets, keepsakes & albums", month: 1, day: 14 },
    { slug: "holi", label: "Holi Edit", copy: "a riot of handmade colour for the festival of colours", month: 2, day: 3 },
    { slug: "raksha-bandhan", label: "Rakhi Edit", copy: "handmade rakhi keepsakes & gifts for your sibling", month: 7, day: 28 },
    { slug: "ganesh-chaturthi", label: "Ganesh Chaturthi Edit", copy: "handmade decor & pottery to welcome Bappa", month: 8, day: 14 },
    { slug: "navratri", label: "Navratri Edit", copy: "handcrafted jewellery & festive blooms for nine nights", month: 9, day: 11 },
    { slug: "diwali", label: "Diwali Edit", copy: "handmade diyas, candles & festive gifting", month: 10, day: 8 },
    { slug: "christmas", label: "Christmas Edit", copy: "cosy handmade gifting for the season of giving", month: 11, day: 25 },
  ];
  let best = null, bestDiff = Infinity;
  for (const d of defs) {
    let dt = new Date(today.getFullYear(), d.month, d.day);
    if (dt < today) dt = new Date(today.getFullYear() + 1, d.month, d.day);
    const diff = dt - today;
    if (diff < bestDiff) { bestDiff = diff; best = d; }
  }
  return best;
}

const RECIPIENT_META = {
  "For Her": "The one who lights up every room",
  "For Him": "For the man who has everything but this",
  "For Husband": "A little love for your forever person",
  "For Wife": "Because she deserves the handmade kind",
  "For Mother": "For the heart that raised yours",
  "For Father": "For the quiet strength you look up to",
  "For Sister": "Your first friend, your forever ally",
  "For Brother": "For the partner in every mischief",
  "For Kids": "Wonder, wrapped by hand",
  "For Friends": "Because good friends deserve good things",
  "For Couple": "For the two who found each other",
  "For Colleague": "Thoughtful, without trying too hard",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [maker, setMaker] = useState(null); // null = loading, false = none
  const [bannerOpen, setBannerOpen] = useState(() => localStorage.getItem("hgk_fest_banner") !== "closed");
  const fest = nextFestival();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/products", { params: { sort: "rating", limit: 8 } }).then(({ data }) => setFeatured(data));
    api.get("/occasions").then(({ data }) => setOccasions(data));
    api.get("/taxonomy").then(({ data }) => setRecipients(data.recipients || []));
    api.get("/storefront/featured-maker")
      .then(({ data }) => setMaker(data.seller ? data : false))
      .catch(() => setMaker(false));
  }, []);

  return (
    <div>
      {/* FESTIVE BANNER */}
      {bannerOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
          className="relative z-40 pt-16"
          data-testid="festive-banner"
        >
          <Link to={`/festival/${fest.slug}`} className="block group" style={{ background: "#8C3D2B" }}>
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-2.5 flex items-center justify-center gap-3 text-sand text-sm">
              <Sparkles className="w-4 h-4 text-terra" />
              <span className="overline">{fest.label} is live</span>
              <span className="hidden sm:inline text-sand/80">— {fest.copy}</span>
              <span className="inline-flex items-center gap-1 group-hover:gap-2 transition-all font-medium">Shop now <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>
          <button
            onClick={() => { setBannerOpen(false); localStorage.setItem("hgk_fest_banner", "closed"); }}
            className="absolute right-3 top-1/2 mt-8 -translate-y-1/2 text-sand/70 hover:text-sand"
            data-testid="close-banner-btn" aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* HERO */}
      <section ref={heroRef} className={`relative ${bannerOpen ? "pt-10 sm:pt-12" : "pt-28 sm:pt-32"} pb-16 overflow-hidden`} data-testid="home-hero">
        {maker === null ? (
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
            <div className="min-h-[55vh] rounded-sm bg-surface/60 animate-pulse" />
          </div>
        ) : (
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="overline text-clay mb-6">
              {maker ? `Featured maker · ${maker.seller.specialty || "handmade in India"}` : "India's handmade gift marketplace"}
            </motion.div>
            {maker ? (
              <h1 className="font-serif text-[11vw] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-ink" data-testid="hero-maker-name">
                <MaskLine delay={0.15}>{maker.seller.name}</MaskLine>
                <MaskLine delay={0.28}><span className="italic text-clay">{(maker.seller.specialty || "handmade").toLowerCase()},</span></MaskLine>
                <MaskLine delay={0.41}>made to be gifted.</MaskLine>
              </h1>
            ) : (
              <h1 className="font-serif text-[13vw] sm:text-7xl lg:text-8xl leading-[0.92] tracking-tighter text-ink">
                <MaskLine delay={0.15}>Gifts that</MaskLine>
                <MaskLine delay={0.28}><span className="italic text-clay">carry</span> a</MaskLine>
                <MaskLine delay={0.41}>heartbeat.</MaskLine>
              </h1>
            )}
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-7 text-mute max-w-md text-base leading-relaxed"
              data-testid="hero-story"
            >
              {maker ? maker.seller.bio : "Discover one-of-a-kind handmade treasures from independent makers across India — resin art, hand-tied bouquets, crochet, candles and more."}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              {maker ? (
                <>
                  <Link to={`/seller/${maker.seller.id}`} className="group inline-flex items-center gap-2 rounded-full bg-clay text-sand px-7 py-3.5 text-sm hover:bg-forest transition-all duration-300 hover:-translate-y-0.5" data-testid="hero-shop-btn">
                    Shop the collection <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                  </Link>
                  <Link to="/shop" className="inline-flex items-center gap-2 rounded-full border border-clay text-clay px-7 py-3.5 text-sm hover:bg-clay hover:text-sand transition-all duration-300" data-testid="hero-browse-btn">
                    Browse all gifts
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/shop" className="group inline-flex items-center gap-2 rounded-full bg-clay text-sand px-7 py-3.5 text-sm hover:bg-forest transition-all duration-300 hover:-translate-y-0.5" data-testid="hero-shop-btn">
                    Explore gifts <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                  </Link>
                  <Link to="/sell" className="inline-flex items-center gap-2 rounded-full border border-clay text-clay px-7 py-3.5 text-sm hover:bg-clay hover:text-sand transition-all duration-300" data-testid="hero-sell-btn">
                    Sell your craft
                  </Link>
                </>
              )}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            {maker && maker.products.length > 0 ? (
              <div className="relative aspect-[4/5]" data-testid="hero-bestsellers">
                <Link to={`/product/${maker.products[0].id}`} className="absolute inset-0 block rounded-sm overflow-hidden shadow-[0_20px_60px_rgba(140,61,43,0.16)]" data-testid="hero-product-0">
                  <motion.img
                    src={optimg(maker.products[0].images?.[0], 900, 72)} alt={maker.products[0].title}
                    style={{ y: imgY, scale: imgScale }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/30 to-transparent" />
                </Link>
                {maker.products[1] && (
                  <motion.div
                    initial={{ opacity: 0, y: 30, rotate: -10 }} animate={{ opacity: 1, y: 0, rotate: -6 }} transition={{ delay: 0.95, duration: 0.7 }}
                    className="absolute -left-4 sm:-left-10 bottom-16 w-32 sm:w-40 z-10"
                  >
                    <Link to={`/product/${maker.products[1].id}`} data-testid="hero-product-1"
                      className="block rounded-sm overflow-hidden border-4 border-sand shadow-[0_18px_40px_rgba(140,61,43,0.28)] hover:rotate-6 hover:scale-105 transition-transform duration-500">
                      <img src={optimg(maker.products[1].images?.[0], 400, 65)} alt={maker.products[1].title} className="w-full aspect-[3/4] object-cover" />
                    </Link>
                  </motion.div>
                )}
                {maker.products[2] && (
                  <motion.div
                    initial={{ opacity: 0, y: -30, rotate: 9 }} animate={{ opacity: 1, y: 0, rotate: 5 }} transition={{ delay: 1.1, duration: 0.7 }}
                    className="absolute -right-3 sm:-right-6 top-6 w-28 sm:w-36 z-10"
                  >
                    <Link to={`/product/${maker.products[2].id}`} data-testid="hero-product-2"
                      className="block rounded-sm overflow-hidden border-4 border-sand shadow-[0_18px_40px_rgba(140,61,43,0.28)] hover:rotate-0 hover:scale-105 transition-transform duration-500">
                      <img src={optimg(maker.products[2].images?.[0], 400, 65)} alt={maker.products[2].title} className="w-full aspect-[3/4] object-cover" />
                    </Link>
                  </motion.div>
                )}
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
                  className="absolute bottom-4 left-4 right-4 bg-sand/90 backdrop-blur rounded-sm px-4 py-3 flex items-center justify-between z-10"
                >
                  <div>
                    <div className="overline text-clay">Best-sellers</div>
                    <div className="font-serif text-lg" data-testid="hero-piece-count">{maker.product_count} handmade pieces · ships across India</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-clay" />
                </motion.div>
              </div>
            ) : (
              <div className="relative aspect-[4/5] rounded-sm overflow-hidden shadow-[0_20px_60px_rgba(140,61,43,0.16)]">
                <motion.img
                  src={HERO} alt="A maker crafting by hand in warm light"
                  style={{ y: imgY, scale: imgScale }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/30 to-transparent" />
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
                  className="absolute bottom-4 left-4 right-4 bg-sand/90 backdrop-blur rounded-sm px-4 py-3 flex items-center justify-between"
                >
                  <div>
                    <div className="overline text-clay">Now live</div>
                    <div className="font-serif text-lg">Handmade gifts · ships across India</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-clay" />
                </motion.div>
              </div>
            )}
          </div>
        </div>
        )}
      </section>

      {/* MARQUEE */}
      <div className="border-y border-line bg-surface py-5">
        <Marquee speed={40} gradient={false} className="marquee-track">
          {["Crafted with love", "Handpicked for you", "Zero listing fees", "Made in India", "Verified reviews", "Prepaid & secure"].map((t, i) => (
            <span key={i} className="font-serif italic text-3xl sm:text-4xl text-clay/80 mx-8 tracking-tight">
              {t} <span className="text-terra not-italic mx-4">✿</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* BROWSE BY INTENT */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <div className="flex items-end justify-between mb-10 gap-4">
            <div>
              <div className="overline text-clay mb-3">Multi-intent browsing</div>
              <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Shop the way<br /> you actually think.</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm text-clay link-underline whitespace-nowrap">
              Browse everything <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {INTENTS.map((it, i) => (
            <Reveal key={it.label} delay={i * 0.05}>
              <Link to={it.to} className="group relative block aspect-[4/3] rounded-sm overflow-hidden" data-testid={`intent-${it.label.toLowerCase().replace(/\s/g,'-')}`}>
                <img src={optimg(it.img, 500, 65)} alt={it.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="font-serif text-xl sm:text-2xl text-sand">{it.label}</span>
                  <span className="w-9 h-9 grid place-items-center rounded-full bg-sand/90 text-clay opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all duration-300">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-clay mb-3">Loved by buyers</div>
              <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Handpicked favourites</h2>
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* SHOP BY RECIPIENT */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 sm:pb-20" data-testid="shop-by-recipient">
        <Reveal>
          <div className="flex items-end justify-between mb-8 gap-4">
            <div>
              <div className="overline text-clay mb-3">Shop by recipient</div>
              <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Who are you<br /> spoiling today?</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm text-clay link-underline whitespace-nowrap">
              Browse everything <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-5 px-5 sm:mx-0 sm:px-0 snap-x recipient-row">
          {recipients.map((r, i) => (
            <Reveal key={r} delay={Math.min(i, 6) * 0.04}>
              <Link
                to={`/shop?recipient=${encodeURIComponent(r)}`}
                data-testid={`home-recipient-${r.toLowerCase().replace(/\s/g, "-")}`}
                className="group snap-start shrink-0 w-[230px] sm:w-[260px] block rounded-sm border border-line bg-surface p-6 hover:border-clay hover:shadow-[0_18px_40px_rgba(140,61,43,0.12)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <span className="overline text-clay/70">Gifts</span>
                  <ArrowUpRight className="w-4 h-4 text-clay opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl tracking-tight mt-6 group-hover:text-clay transition-colors">{r}</h3>
                <p className="text-sm text-mute mt-3 leading-relaxed">{RECIPIENT_META[r] || "Handmade gifts they'll treasure"}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SHOP BY OCCASION */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-4">
        <Reveal>
          <div className="overline text-clay mb-5">Shop by occasion</div>
          <div className="flex flex-wrap gap-3">
            {occasions.map((o) => (
              <Link key={o.slug} to={`/occasion/${o.slug}`} data-testid={`home-occ-${o.slug}`}
                className="group inline-flex items-center gap-2 rounded-full border border-line bg-surface px-6 py-3 hover:border-clay hover:bg-clay hover:text-sand transition-colors">
                <span className="font-serif text-lg">{o.name}</span>
                <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* MANIFESTO */}
      <section className="bg-forest text-sand py-20 sm:py-32 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative aspect-[4/5] rounded-sm overflow-hidden">
                <img src={MANI} alt="Hands weaving craft" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="overline text-terra mb-8">Why handmadegiftkart</div>
            <div className="space-y-10">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.08}>
                  <div className="relative flex gap-6 sm:gap-10">
                    <span className="font-serif text-6xl sm:text-8xl font-light text-sand/15 leading-none select-none">{c.n}</span>
                    <div className="pt-2 sm:pt-4">
                      <h3 className="font-serif text-2xl sm:text-3xl tracking-tight">{c.t}</h3>
                      <p className="text-sand/70 mt-2 max-w-lg leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SELLER CTA */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <div className="relative rounded-sm bg-surface border border-line px-6 sm:px-16 py-14 sm:py-20 text-center overflow-hidden">
            <div className="overline text-clay mb-4">Makers wanted</div>
            <h2 className="font-serif text-3xl sm:text-6xl tracking-tight max-w-3xl mx-auto leading-[1.02]">
              Turn your craft into a<br /> <span className="italic text-clay">thriving little shop.</span>
            </h2>
            <p className="text-mute mt-5 max-w-md mx-auto">Free unlimited listings. No subscription. We only earn a small commission when you do.</p>
            <Link to="/sell" className="inline-flex items-center gap-2 rounded-full bg-clay text-sand px-8 py-4 mt-8 text-sm hover:bg-forest transition-all duration-300 hover:-translate-y-0.5" data-testid="cta-sell-btn">
              Become a seller <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
